import { cn } from '@solinkcorp/solink-ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';

const YEAR_PAGE = 12;

export type PickerView = 'day' | 'month' | 'year';

function yearPageContaining(year: number, minYear: number) {
  const offset = Math.max(0, year - minYear);
  return minYear + Math.floor(offset / YEAR_PAGE) * YEAR_PAGE;
}

/** Month grid, then a year grid. Clicking the header moves one level up, matching mobile. */
export function PeriodJump({
  viewMode,
  browseYear,
  minYear,
  maxYear,
  minDay,
  maxDay,
  selectedMonth,
  localeCode,
  onBrowseYear,
  onShowMonths,
  onShowYears,
  onPickMonth,
}: {
  viewMode: Exclude<PickerView, 'day'>;
  browseYear: number;
  minYear: number;
  maxYear: number;
  minDay: Date | undefined;
  maxDay: Date;
  selectedMonth: Date;
  localeCode: string;
  onBrowseYear: (year: number) => void;
  onShowMonths: () => void;
  onShowYears: () => void;
  onPickMonth: (monthIndex: number) => void;
}) {
  const [yearPageStart, setYearPageStart] = useState(() => yearPageContaining(browseYear, minYear));
  const isYearView = viewMode === 'year';
  const pageEnd = Math.min(maxYear, yearPageStart + YEAR_PAGE - 1);
  const canGoPrev = isYearView ? yearPageStart > minYear : browseYear > minYear;
  const canGoNext = isYearView ? yearPageStart + YEAR_PAGE <= maxYear : browseYear < maxYear;

  const months = Array.from({ length: 12 }, (_, index) => ({
    index,
    label: new Date(browseYear, index, 1).toLocaleString(localeCode, { month: 'long' }),
  }));
  const years = Array.from({ length: YEAR_PAGE }, (_, index) => yearPageStart + index);

  return (
    <div className="flex min-h-[21rem] flex-col">
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          className="flex size-9 items-center justify-center rounded-md hover:bg-background-muted disabled:pointer-events-none disabled:opacity-40"
          disabled={!canGoPrev}
          onClick={() =>
            isYearView
              ? setYearPageStart(Math.max(minYear, yearPageStart - YEAR_PAGE))
              : onBrowseYear(browseYear - 1)
          }
        >
          <ChevronLeft className="size-4" />
        </button>
        <button
          type="button"
          className="flex items-center gap-1 rounded-md px-3 py-1 font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
          onClick={() => {
            if (isYearView) {
              onShowMonths();
              return;
            }
            setYearPageStart(yearPageContaining(browseYear, minYear));
            onShowYears();
          }}
        >
          {isYearView ? `${yearPageStart} – ${pageEnd}` : browseYear}
        </button>
        <button
          type="button"
          className="flex size-9 items-center justify-center rounded-md hover:bg-background-muted disabled:pointer-events-none disabled:opacity-40"
          disabled={!canGoNext}
          onClick={() =>
            isYearView ? setYearPageStart(yearPageStart + YEAR_PAGE) : onBrowseYear(browseYear + 1)
          }
        >
          <ChevronRight className="size-4" />
        </button>
      </div>
      <div className="grid flex-1 grid-cols-4 content-center gap-2">
        {isYearView
          ? years.map((year) => (
              <PeriodOption
                key={year}
                label={`${year}`}
                selected={year === selectedMonth.getFullYear()}
                disabled={year < minYear || year > maxYear}
                onClick={() => {
                  onBrowseYear(year);
                  onShowMonths();
                }}
              />
            ))
          : months.map((monthOption) => {
              const monthStart = new Date(browseYear, monthOption.index, 1);
              const monthEnd = new Date(browseYear, monthOption.index + 1, 0);
              return (
                <PeriodOption
                  key={monthOption.index}
                  label={monthOption.label}
                  selected={
                    browseYear === selectedMonth.getFullYear() &&
                    monthOption.index === selectedMonth.getMonth()
                  }
                  disabled={(minDay !== undefined && monthEnd < minDay) || monthStart > maxDay}
                  onClick={() => onPickMonth(monthOption.index)}
                />
              );
            })}
      </div>
    </div>
  );
}

function PeriodOption({
  label,
  selected,
  disabled,
  onClick,
}: {
  label: string;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'rounded-lg px-2 py-3 text-center transition-colors body-sm hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40',
        selected && 'bg-primary font-medium text-primary-foreground hover:bg-primary',
      )}
    >
      {label}
    </button>
  );
}
